import classNames from 'classnames';
import i18n from 'src/utils/translate';
import { debounce } from 'lodash';
import Button, { ButtonType } from 'src/components/buttons/button';
import { followCommunity, unfollowCommunity } from 'src/modules/community/shared/actions';
import { followPost, unfollowPost } from 'src/modules/community/singlePost/actions/actions';
import { followUser, unfollowUser } from 'src/modules/user/profile/actions';
import { closeModal, showModal, showSignupModal } from 'src/modules/shared/modal/actions';
import { ModalType } from 'src/constants/modalTypes';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, FootfallContextType, Element, Section } from 'src/constants/footfall';
import { getShowSavePostModalPreference } from 'src/utils/showSavePostModalPreference';
import { Community } from 'src/constants/types';
import { useState } from 'react';
import { useDispatch, useSelector } from 'src/store/store';
import { ModalProps } from 'src/modules/shared/modal/components/defaultModal';
import FollowSuccessMessage from 'src/modules/community/shared/components/alert/followSuccessMessage';
import { Icon } from 'src/components/icon';

type Props = {
  buttonText?: string;
  buttonType?: ButtonType;
  className?: string;
  community?: Community;
  dataTestId?: string;
  followIds?: number[];
  followType: 'user' | 'post' | 'community';
  fullWidth?: boolean;
  isDisabled?: boolean;
  isFollowing?: boolean;
  noIcon?: boolean;
  role?: string;
  signupContext?: FootfallContextType;
  size?: 'sm' | 'lg';
  tabIndex?: number;
  trackLabel?: string;
  onFollowClick?: () => void;
  onSignupSuccess?: () => void;
}
export default function FollowButton({
  buttonText = i18n.t('Join'),
  buttonType = 'secondary',
  className,
  community,
  dataTestId,
  followIds = [],
  followType,
  fullWidth = false,
  isDisabled = false,
  isFollowing,
  noIcon = false,
  role,
  signupContext = {
    element: Element.FOLLOW,
    section: Section.UNTRACKED,
  },
  onFollowClick = () => { },
  onSignupSuccess,
  tabIndex,
  trackLabel = 'N/A',
  size,
}: Props) {
  const dispatch = useDispatch();

  const currentUser = useSelector(({ context }) => context.currentUser);

  const [hovered, setHovered] = useState(false);

  const triggerGroupLeavingModal = () => {
    const successMessage = i18n.t('You have left %1$s').replace('%1$s', community.name) + '!';
    const modalProps: ModalProps = {
      acceptButtonText: i18n.t('Leave Community'),
      cancelButtonText: i18n.t('Cancel'),
      children: i18n.t('Leaving the community will affect your news feed, emails, notifications and badges. Your contributions will remain on the community unless you delete them.'),
      title: i18n.t('Are you sure you want to do this?'),
      onAccept: () => {
        dispatch(unfollowCommunity({ communitySlug: community.slug, successMessage }));
        dispatch(closeModal());
      },
    };
    dispatch(showModal({ modalType: ModalType.Default, modalProps }));
  };

  const onClick = () => {
    if (!currentUser) {
      dispatch(showSignupModal({
        community: followType === 'community' ? community : null,
        onSignupSuccess,
      }));
      trackFootfallEvent(Event.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: { ...signupContext },
      });
    } else {
      if (followType === 'post') {
        if (isFollowing) {
          dispatch(unfollowPost(followIds[0]));
        } else {
          dispatch(followPost(followIds[0]));
          if (getShowSavePostModalPreference()) {
            dispatch(showModal({ modalType: ModalType.SavePost }));
          }
        }
      } else if (followType === 'community') {
        if (isFollowing) {
          triggerGroupLeavingModal();
        } else {
          dispatch(followCommunity({
            communitySlug: community.slug,
            successMessage: <FollowSuccessMessage communityName={community.name} />,
          }));
        }
      } else if (followType === 'user') {
        if (isFollowing) {
          dispatch(unfollowUser(followIds[0], i18n.t('You unfollowed') + ` ${trackLabel}`));
        } else {
          dispatch(followUser(followIds[0], i18n.t('You are now following') + `  ${trackLabel}`));
        }
      }
      onFollowClick();
    }
  };

  const debouncedClick = debounce(onClick, 300);

  const iconClasses = {
    'icon--success': true,
    'icon--danger': isFollowing && hovered && !noIcon,
  };

  let followingText;
  let followingAriaLabel;

  switch (followType) {
    case 'post':
      followingText = hovered ? i18n.t('Unsave') : i18n.t('Saved');
      followingAriaLabel = i18n.t('Unsave post');
      break;
    case 'user':
      followingText = hovered ? i18n.t('Unfollow') : i18n.t('Following');
      followingAriaLabel = i18n.t('Unfollow user');
      break;
    case 'community':
    default:
      followingText = noIcon || hovered ? i18n.t('Leave') : i18n.t('');
      followingAriaLabel = i18n.t('Leave community');
  }

  return (
    <Button
      ariaLabel={isFollowing ? followingAriaLabel : buttonText}
      className={className}
      dataTestId={dataTestId || 'follow-button'}
      disabled={isDisabled}
      fullWidth={fullWidth}
      icon={getIcon({ isFollowing, hovered, noIcon })}
      iconClassName={classNames(iconClasses)}
      role={role}
      size={size}
      tabIndex={tabIndex}
      text={isFollowing ? followingText : buttonText}
      type={buttonType || 'secondary'}
      onClick={debouncedClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    />
  );
}

function getIcon({ isFollowing, hovered, noIcon }: { isFollowing: boolean; hovered: boolean; noIcon: boolean; }): Icon | null {
  if (noIcon)
    return null;

  if (!isFollowing) {
    return 'plus';
  } else {
    if (hovered) {
      return 'cancel-5';
    }
    return 'ok';
  }
}
